
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Separator } from '@/components/ui/separator';
import { Users, Briefcase } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';
import { EntityLookup, type EntityOption } from '@/components/ui/entity-lookup';
import { listUsers } from '@/lib/userApi';
import { listTeams } from '@/lib/teamApi';
import { searchBusinessUnits } from '@/lib/businessUnitsApi';

interface AssignmentSectionProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function AssignmentSection({ form, isViewMode = false }: AssignmentSectionProps) {
  // Search function for users lookup
  const searchUsers = async (query: string): Promise<EntityOption[]> => {
    const response = await listUsers({ search: query, limit: 20 });
    return response.users.map(user => ({
      value: user.id,
      label: user.fullName || user.username,
      subtitle: user.email,
      metadata: { 
        jobTitle: user.jobTitle,
        department: user.department?.name 
      }
    }));
  };

  // Search function for teams lookup
  const searchTeams = async (query: string): Promise<EntityOption[]> => {
    const response = await listTeams({ search: query, limit: 20 });
    return response.data.map(team => ({
      value: team.id,
      label: team.name,
      subtitle: team.description || undefined,
      metadata: { 
        code: team.code,
        memberCount: team.memberCount 
      }
    }));
  };

  // Search function for business units lookup
  const searchBUs = async (query: string): Promise<EntityOption[]> => {
    const results = await searchBusinessUnits(query, 20);
    return results.map(bu => ({
      value: bu.id,
      label: bu.name,
      subtitle: bu.code ? `Código: ${bu.code}` : undefined,
      metadata: { 
        level: bu.level,
        parentId: bu.parentId,
        isActive: bu.isActive 
      }
    }));
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Users className="h-4 w-4" />
        <AlertDescription>
          Gestión de responsables, equipos y unidades de negocio
        </AlertDescription>
      </Alert>

      {/* Requester */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <UserIcon className="h-4 w-4" /> Reportante
        </h3>
        <FormField
          control={form.control}
          name="requesterUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Usuario Reportante</FormLabel>
              <FormControl>
                <EntityLookup
                  value={field.value}
                  onChange={field.onChange}
                  onSearch={searchUsers}
                  placeholder="Buscar usuario..."
                  searchPlaceholder="Nombre o email..."
                  emptyMessage="No se encontraron usuarios"
                  allowClear
                  disabled={isViewMode}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Assignment */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Briefcase className="h-4 w-4" /> Asignación Técnica
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="assignedToUserId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Técnico Asignado</FormLabel>
                <FormControl>
                  <EntityLookup
                    value={field.value}
                    onChange={field.onChange}
                    onSearch={searchUsers}
                    placeholder="Buscar técnico..."
                    searchPlaceholder="Nombre o email..."
                    emptyMessage="No se encontraron usuarios"
                    allowClear
                    disabled={isViewMode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="assignedAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Asignación</FormLabel>
                <FormControl>
                  <DateTimePicker
                    value={field.value || null}
                    onChange={field.onChange}
                    label="Fecha"
                    placeholder="Seleccionar fecha"
                    id="assignedAt"
                    disabled={isViewMode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <Separator />

      {/* Ownership */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Building2Icon className="h-4 w-4" /> Propiedad y Organización
        </h3>

        <FormField
          control={form.control}
          name="ownerUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Owner del Incidente</FormLabel>
              <FormControl>
                <EntityLookup
                  value={field.value}
                  onChange={field.onChange}
                  onSearch={searchUsers}
                  placeholder="Buscar owner..."
                  searchPlaceholder="Nombre o email..."
                  emptyMessage="No se encontraron usuarios"
                  allowClear
                  disabled={isViewMode}
                />
              </FormControl>
              <FormDescription>Responsable final ante el negocio</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="owningTeamId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Equipo Propietario</FormLabel>
                <FormControl>
                  <EntityLookup
                    value={field.value}
                    onChange={field.onChange}
                    onSearch={searchTeams}
                    placeholder="Buscar equipo..."
                    searchPlaceholder="Nombre o código..."
                    emptyMessage="No se encontraron equipos"
                    allowClear
                    disabled={isViewMode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="owningBuId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unidad de Negocio</FormLabel>
                <FormControl>
                  <EntityLookup
                    value={field.value}
                    onChange={field.onChange}
                    onSearch={searchBUs}
                    placeholder="Buscar unidad..."
                    searchPlaceholder="Nombre o código..."
                    emptyMessage="No se encontraron unidades"
                    allowClear
                    disabled={isViewMode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}

function UserIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function Building2Icon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  )
}
